import { BsTrash } from 'react-icons/bs';
import type { ColDef, ICellRendererParams } from '../../../components/ui/table';
import { formatAuditDateTime } from '../../../lib/auditColumnDefs';
import type { ExcelColumn } from '../../../utils/excelExport';
import { collectUniqueScalarValues } from '../../../components/shared/GridColumnFilterPanel';
import {
  CategoryChipCellRenderer,
  EmptyGridCell,
  GridCellWrap,
  StatusBadgeCellRenderer,
  SubtitleCellRenderer,
} from '../../../components/shared/agGridCells';
import { Button } from '../../../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { cn } from '../../../lib/utils';
import type {
  DccMarkupRateColumnFilterKey,
  DccMarkupRateGridContext,
  DccMarkupRateGridRow,
  DccMarkupRateRow,
} from '../models/types';

type DccMarkupRateFilterColumnConfig = {
  key: DccMarkupRateColumnFilterKey;
  label: string;
  type?: 'select' | 'dateRange';
  getOptions?: (rows: DccMarkupRateRow[]) => string[];
};

export const DCC_MARKUP_RATE_FILTER_COLUMNS: DccMarkupRateFilterColumnConfig[] = [
  {
    key: 'pair',
    label: 'Pair',
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => `${row.fromCurrency} → ${row.toCurrency}`),
  },
  {
    key: 'rateLabel',
    label: 'Rate',
    getOptions: (rows) =>
      collectUniqueScalarValues(
        rows,
        (row) => `1 ${row.fromCurrency} = ${row.rate} ${row.toCurrency}`,
      ),
  },
  {
    key: 'status',
    label: 'Status',
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.status || 'UNKNOWN')),
  },
  {
    key: 'notes',
    label: 'Notes',
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.notes || '—')),
  },
  {
    key: 'createdAt',
    label: 'Created At',
    type: 'dateRange',
  },
];

export const DCC_MARKUP_RATE_COLUMN_LABELS = Object.fromEntries(
  DCC_MARKUP_RATE_FILTER_COLUMNS.map(({ key, label }) => [key, label]),
) as Record<DccMarkupRateColumnFilterKey, string>;

function columnLabel(key: DccMarkupRateColumnFilterKey) {
  return DCC_MARKUP_RATE_COLUMN_LABELS[key];
}

/** Same columns and visual order as the DCC rates grid (Action excluded). Status is pinned right. */
export const DCC_MARKUP_RATE_EXPORT_COLUMNS: ExcelColumn[] = [
  { header: columnLabel('pair'), key: 'pair' },
  { header: columnLabel('rateLabel'), key: 'rateLabel' },
  { header: columnLabel('notes'), key: 'notes' },
  { header: columnLabel('createdAt'), key: 'createdAt' },
  { header: columnLabel('status'), key: 'status' },
];

export function buildDccMarkupRateColumnFilterFields(sourceRows: DccMarkupRateRow[]) {
  return DCC_MARKUP_RATE_FILTER_COLUMNS.map(({ key, label, getOptions, type }) => ({
    key,
    label,
    type: type ?? 'select',
    options: type === 'dateRange' ? [] : (getOptions?.(sourceRows) ?? []),
  }));
}

function DccRateActionCellRenderer(params: ICellRendererParams<DccMarkupRateGridRow>) {
  const row = params.data;
  const context = params.context as DccMarkupRateGridContext | undefined;
  if (!row || row.status !== 'ACTIVE' || !context?.onDeactivate) {
    return (
      <GridCellWrap>
        <EmptyGridCell />
      </GridCellWrap>
    );
  }

  return (
    <div
      className="flex h-full flex-wrap items-center justify-end gap-1 py-1"
      onClick={(e) => e.stopPropagation()}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className={cn(
              'tm-compact-icon-btn shrink-0 hover:border-destructive hover:text-destructive hover:bg-destructive/10',
            )}
            aria-label="Deactivate rate"
            onClick={() => context.onDeactivate(row)}
          >
            <BsTrash />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Deactivate rate</TooltipContent>
      </Tooltip>
    </div>
  );
}

export function createDccMarkupRateColumnDefs(): ColDef<DccMarkupRateGridRow>[] {
  return [
    {
      field: 'pair',
      colId: 'pair',
      headerName: columnLabel('pair'),
      flex: 1,
      minWidth: 140,
      cellRenderer: CategoryChipCellRenderer,
    },
    {
      field: 'rateLabel',
      colId: 'rateLabel',
      headerName: columnLabel('rateLabel'),
      flex: 1.5,
      minWidth: 220,
      cellRenderer: SubtitleCellRenderer,
    },
    {
      field: 'status',
      colId: 'status',
      headerName: columnLabel('status'),
      width: 108,
      cellRenderer: StatusBadgeCellRenderer,
      pinned:'right'
    },
    {
      field: 'notes',
      colId: 'notes',
      headerName: columnLabel('notes'),
      flex: 1.5,
      minWidth: 180,
      filterValueGetter: (params) => String(params.data?.notes ?? ''),
      valueFormatter: (params) => params.value || '—',
      cellClass: 'text-muted-foreground',
    },
    {
      colId: 'createdAt',
      headerName: columnLabel('createdAt'),
      width: 180,
      cellClass: 'text-muted-foreground',
      valueGetter: (params) => params.data?.createdAt ?? null,
      valueFormatter: (params) => formatAuditDateTime(String(params.value ?? '')),
      filter: 'agDateColumnFilter',
      filterParams: {
        filterOptions: ['equals', 'inRange'],
        maxNumConditions: 1,
        buttons: ['clear', 'apply'],
        closeOnApply: true,
      },
    },
    {
      headerName: 'Action',
      width: 130,
      sortable: false,
      filter: false,
      cellRenderer: DccRateActionCellRenderer,
      pinned: 'right',
    },
  ];
}
